/**
 * 1.5.3: 错误消息翻译工具
 * 将系统错误消息（主要来自 Supabase Auth 和数据库 RPC 函数）转换为多语言
 */
import type { TFunction } from 'i18next';

const AUTH_TRANSLATION_KEYS = [
  'authErrors.invalidCredentials',
  'authErrors.emailNotConfirmed',
  'authErrors.userAlreadyRegistered',
  'authErrors.credentialTooShort',
  'authErrors.invalidEmailFormat',
  'authErrors.signupDisabled',
  'authErrors.emailRateLimitExceeded',
  'authErrors.rateLimitExceeded',
  'authErrors.networkError',
  'authErrors.serviceUnavailable',
  'authErrors.invalidOrExpiredResetToken',
] as const;

const API_TRANSLATION_KEYS = [
  'apiErrors.invalidInvitationLink',
  'apiErrors.invitationNoLongerValid',
  'apiErrors.invitationExpired',
  'apiErrors.invitationMaxUsage',
  'apiErrors.invitationWrongEmail',
  'apiErrors.alreadyMember',
  'apiErrors.organizationMaxMembers',
  'apiErrors.userAlreadyMember',
  'apiErrors.userNotFound',
  'apiErrors.onlyOwnersCanInvite',
  'apiErrors.onlyOwnersCanRemove',
  'apiErrors.onlyOwnersCanChangeRole',
  'apiErrors.cannotRemoveOwner',
  'apiErrors.cannotLeaveAsOnlyOwner',
  'apiErrors.organizationNeedsOwner',
  'apiErrors.accessDenied',
  'apiErrors.organizationNotFound',
  'apiErrors.memberNotFound',
  'apiErrors.userAlreadyHasOrg',
  'apiErrors.invalidRole',
  'apiErrors.invalidCompanySize',
  'apiErrors.invalidPlanType',
  'apiErrors.onlyOwnerCanUpgrade',
  'apiErrors.knowledgeBaseNotFound',
  'apiErrors.onlyCreatorOrOwnerCanShare',
  'apiErrors.notAuthenticated',
  'apiErrors.noOrganizationFound',
] as const;

export type AuthTranslationKey = (typeof AUTH_TRANSLATION_KEYS)[number];
export type ApiTranslationKey = (typeof API_TRANSLATION_KEYS)[number];
export type TranslationKey = AuthTranslationKey | ApiTranslationKey;
export type TranslationEntry<K extends TranslationKey> = readonly [key: K, fallback: string];
type TranslationPatternMap<K extends TranslationKey> = Record<string, TranslationEntry<K>>;

const AUTH_ERROR_PATTERNS = {
  'Invalid login credentials': ['authErrors.invalidCredentials', '邮箱或密码错误'],
  'Email not confirmed': ['authErrors.emailNotConfirmed', '邮箱尚未验证，请查收验证邮件'],
  'User already registered': ['authErrors.userAlreadyRegistered', '该邮箱已被注册'],
  'Password should be at least 6 characters': ['authErrors.credentialTooShort', '密码至少8位，须包含字母、数字和特殊符号'],
  'Password must be at least 8 characters with letters, numbers, and symbols': ['authErrors.credentialTooShort', '密码至少8位，须包含字母、数字和特殊符号'],
  'Unable to validate email address: invalid format': ['authErrors.invalidEmailFormat', '邮箱格式不正确'],
  'Signup is disabled': ['authErrors.signupDisabled', '注册功能已禁用'],
  'Email rate limit exceeded': ['authErrors.emailRateLimitExceeded', '邮件发送次数过多，请稍后再试'],
  'For security purposes, you can only request this once every 60 seconds': ['authErrors.rateLimitExceeded', '出于安全考虑，请60秒后再试'],
  'Too many password reset requests': ['authErrors.rateLimitExceeded', '出于安全考虑，请60秒后再试'],
  'Too many password reset attempts': ['authErrors.rateLimitExceeded', '出于安全考虑，请60秒后再试'],
  'Failed to fetch': ['authErrors.networkError', '网络连接异常，请检查网络后重试'],
  'NetworkError when attempting to fetch resource': ['authErrors.networkError', '网络连接异常，请检查网络后重试'],
  'Network request failed': ['authErrors.networkError', '网络连接异常，请检查网络后重试'],
  'fetch failed': ['authErrors.networkError', '网络连接异常，请检查网络后重试'],
  'Load failed': ['authErrors.networkError', '网络连接异常，请检查网络后重试'],
  'HTTP 5': ['authErrors.serviceUnavailable', '服务暂时不可用，请稍后重试'],
  'HTTP 502': ['authErrors.serviceUnavailable', '服务暂时不可用，请稍后重试'],
  'HTTP 503': ['authErrors.serviceUnavailable', '服务暂时不可用，请稍后重试'],
  'HTTP 504': ['authErrors.serviceUnavailable', '服务暂时不可用，请稍后重试'],
  'Invalid or expired reset token': ['authErrors.invalidOrExpiredResetToken', '重置链接无效或已过期'],
} as const satisfies TranslationPatternMap<AuthTranslationKey>;

const API_ERROR_PATTERNS = {
  // 邀请相关
  'Invalid invitation link': ['apiErrors.invalidInvitationLink', '无效的邀请链接'],
  'This invitation is no longer valid': ['apiErrors.invitationNoLongerValid', '此邀请已失效'],
  'This invitation has expired': ['apiErrors.invitationExpired', '邀请链接已过期'],
  'This invitation has reached its maximum usage limit': ['apiErrors.invitationMaxUsage', '此邀请链接已达到最大使用次数'],
  'This invitation is for a specific email address': ['apiErrors.invitationWrongEmail', '此邀请链接仅限特定邮箱使用'],
  'You are already a member of this organization': ['apiErrors.alreadyMember', '您已经是该组织的成员'],
  'This organization has reached its maximum member limit': ['apiErrors.organizationMaxMembers', '该组织已达到成员人数上限'],
  'User is already a member of this organization': ['apiErrors.userAlreadyMember', '该用户已是组织成员'],
  'User not found with email': ['apiErrors.userNotFound', '未找到该邮箱对应的用户'],
  // 权限相关
  'Only owners can invite members': ['apiErrors.onlyOwnersCanInvite', '仅所有者可以邀请成员'],
  'Only owners can remove other members': ['apiErrors.onlyOwnersCanRemove', '仅所有者可以移除成员'],
  'Only owners can change member roles': ['apiErrors.onlyOwnersCanChangeRole', '仅所有者可以更改成员角色'],
  'Cannot remove another owner': ['apiErrors.cannotRemoveOwner', '无法移除其他所有者，请先更改其角色'],
  'Cannot leave: you are the only owner': ['apiErrors.cannotLeaveAsOnlyOwner', '您是唯一的所有者，无法离开'],
  'organization must have at least one owner': ['apiErrors.organizationNeedsOwner', '组织必须至少有一位所有者'],
  'Access denied: not a member of this organization': ['apiErrors.accessDenied', '访问被拒绝：您不是该组织的成员'],
  'Not a member of this organization': ['apiErrors.accessDenied', '访问被拒绝：您不是该组织的成员'],
  // 组织相关
  'Organization not found': ['apiErrors.organizationNotFound', '未找到组织'],
  'Member not found': ['apiErrors.memberNotFound', '未找到该成员'],
  'User already has an organization': ['apiErrors.userAlreadyHasOrg', '用户已有组织'],
  'User already belongs to an organization': ['apiErrors.userAlreadyHasOrg', '用户已有组织'],
  'Invalid role': ['apiErrors.invalidRole', '无效的角色'],
  'Invalid company size': ['apiErrors.invalidCompanySize', '无效的企业规模'],
  'Invalid plan type': ['apiErrors.invalidPlanType', '无效的套餐类型'],
  'Only owner can upgrade plan': ['apiErrors.onlyOwnerCanUpgrade', '仅所有者可以升级套餐'],
  // 知识库相关
  'Knowledge base not found': ['apiErrors.knowledgeBaseNotFound', '未找到知识库'],
  'Only the creator or organization owner can change sharing settings': ['apiErrors.onlyCreatorOrOwnerCanShare', '仅创建者或组织所有者可以更改共享设置'],
  // 通用
  'Not authenticated': ['apiErrors.notAuthenticated', '未登录'],
  'No organization found': ['apiErrors.noOrganizationFound', '未找到组织'],
} as const satisfies TranslationPatternMap<ApiTranslationKey>;

function tr<K extends TranslationKey>(t: TFunction, key: K, fallback: string): string {
  return t(key, { defaultValue: fallback }) as string;
}

/**
 * 翻译认证相关的错误消息
 * @param errorMessage 原始错误消息
 * @param t i18n 翻译函数
 * @returns 翻译后的错误消息
 */
export function translateAuthError(errorMessage: string, t: TFunction): string {
  for (const [pattern, [translationKey, fallback]] of Object.entries(AUTH_ERROR_PATTERNS)) {
    if (errorMessage.includes(pattern)) {
      return tr(t, translationKey, fallback);
    }
  }
  return errorMessage;
}

/**
 * 翻译 API/组织相关的错误消息
 * @param errorMessage 原始错误消息
 * @param t i18n 翻译函数
 * @returns 翻译后的错误消息
 */
export function translateApiError(errorMessage: string, t: TFunction): string {
  for (const [pattern, [translationKey, fallback]] of Object.entries(API_ERROR_PATTERNS)) {
    if (errorMessage.includes(pattern)) {
      return tr(t, translationKey, fallback);
    }
  }
  return errorMessage;
}

/**
 * 翻译任意错误消息（先尝试认证错误，再尝试 API 错误）
 * @param errorMessage 原始错误消息
 * @param t i18n 翻译函数
 * @returns 翻译后的错误消息
 */
export function translateError(errorMessage: string, t: TFunction): string {
  // 先尝试翻译认证错误
  const authTranslated = translateAuthError(errorMessage, t);
  if (authTranslated !== errorMessage) {
    return authTranslated;
  }
  
  // 再尝试翻译 API 错误
  return translateApiError(errorMessage, t);
}
